import React from "react";

const THROTTLE_DELAY = 500;

interface Props<Value> {
	value?: Value | undefined;
	onChangeValue?(value: Value | undefined): void;
	onBlur?(event: React.FocusEvent<HTMLInputElement>): unknown;
	onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): unknown;
}

type PropsWithRef<T> = React.PropsWithoutRef<T> &
	React.RefAttributes<HTMLInputElement>;

type PropsWithInnerRef<T> = T & {
	innerRef: React.Ref<HTMLInputElement>;
};

interface State<Value> {
	cachedValue?: Value;
}

export function createAsyncInput<Value, InputProps extends Props<Value>>(
	SyncInput: React.ComponentType<InputProps>
): React.ForwardRefExoticComponent<PropsWithRef<InputProps>> {
	type AsyncInputProps = PropsWithInnerRef<InputProps>;
	type AsyncInputState = State<Value>;

	class AsyncInput extends React.PureComponent<
		AsyncInputProps,
		AsyncInputState
	> {
		private onChangeTimeout = 0;

		constructor(props: AsyncInputProps) {
			super(props);
			this.state = { cachedValue: props.value };
		}

		componentDidUpdate(prevProps: AsyncInputProps): void {
			if (this.props.value !== prevProps.value) {
				this.setState({ cachedValue: this.props.value });
			}
		}

		render(): React.ReactElement {
			const { innerRef, ...props } = this.props;
			return (
				<SyncInput
					{...(props as unknown as InputProps)}
					ref={innerRef}
					value={this.state.cachedValue}
					onChangeValue={this.onChangeValue}
					onBlur={this.onBlur}
					onKeyDown={this.onKeyDown}
				/>
			);
		}

		private onChangeValue = (value: Value): void => {
			this.setState({ cachedValue: value });
			window.clearTimeout(this.onChangeTimeout);
			this.onChangeTimeout = window.setTimeout(
				this.publishValue,
				THROTTLE_DELAY
			);
		};

		private onKeyDown = (
			event: React.KeyboardEvent<HTMLInputElement>
		): void => {
			if (this.props.onKeyDown) {
				this.props.onKeyDown(event);
			}
			if (event.key === "Enter") {
				window.clearTimeout(this.onChangeTimeout);
				this.publishValue();
			}
		};

		private onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
			if (this.props.onBlur) {
				this.props.onBlur(event);
			}
			window.clearTimeout(this.onChangeTimeout);
			this.publishValue();
		};

		private publishValue = (): void => {
			if (this.props.onChangeValue) {
				this.props.onChangeValue(this.state.cachedValue);
			}
		};
	}

	return React.forwardRef<HTMLInputElement, InputProps>((props, ref) => (
		<AsyncInput {...(props as InputProps)} innerRef={ref} />
	));
}
